import { skipToken } from "@reduxjs/toolkit/query";
import type {
  GuruIntegrationRequest,
  KustomerIntegrationRequest,
} from "@repo/app-types/entities/integration";
import type {
  KnowledgeBaseSyncActiveRun,
  KnowledgeBaseSyncIntegration,
  KnowledgeBaseSyncProvider,
  KnowledgeBaseSyncRunResponse,
  KnowledgeBaseSyncStatusResponse,
} from "@repo/app-types/entities/knowledgeBaseSync";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

interface KnowledgeBaseSyncIntegrationConfiguration {
  provider: KnowledgeBaseSyncProvider;
  configuration: GuruIntegrationRequest | KustomerIntegrationRequest;
}

type UpdateKnowledgeBaseSyncIntegrationArgs =
  KnowledgeBaseSyncIntegrationConfiguration & {
    integrationId: string;
  };

interface KnowledgeBaseSyncIntegrationActionArgs {
  provider: KnowledgeBaseSyncProvider;
  integrationId: string;
}

const ACTIVE_SYNC_POLL_INTERVAL_MS = 3_000;

export const knowledgeBaseSyncIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    knowledgeBaseSyncIntegrationStatus: builder.query<
      KnowledgeBaseSyncStatusResponse,
      KnowledgeBaseSyncProvider
    >({
      query: (provider) => `/integrations/${provider}`,
      providesTags: (_result, _error, provider) => [
        buildEntityTag(TagType.KnowledgeBaseSyncIntegration, provider),
      ],
    }),

    createKnowledgeBaseSyncIntegration: builder.mutation<
      KnowledgeBaseSyncIntegration,
      KnowledgeBaseSyncIntegrationConfiguration,
      { integration: KnowledgeBaseSyncIntegration }
    >({
      query: ({ provider, configuration }) => ({
        method: "POST",
        url: `/integrations/${provider}`,
        body: configuration,
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: (_result, _error, { provider }) => [
        buildEntityTag(TagType.KnowledgeBaseSyncIntegration, provider),
        TagType.KnowledgeBase,
        TagType.KnowledgeBaseAttachment,
      ],
    }),

    updateKnowledgeBaseSyncIntegration: builder.mutation<
      KnowledgeBaseSyncIntegration,
      UpdateKnowledgeBaseSyncIntegrationArgs,
      { integration: KnowledgeBaseSyncIntegration }
    >({
      query: ({ integrationId, configuration }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: configuration,
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: (_result, _error, { provider }) => [
        buildEntityTag(TagType.KnowledgeBaseSyncIntegration, provider),
        TagType.KnowledgeBase,
        TagType.KnowledgeBaseAttachment,
      ],
    }),

    deleteKnowledgeBaseSyncIntegration: builder.mutation<
      void,
      KnowledgeBaseSyncIntegrationActionArgs
    >({
      query: ({ integrationId }) => ({
        method: "DELETE",
        url: `/integrations/${integrationId}`,
      }),
      invalidatesTags: (_result, _error, { provider }) => [
        buildEntityTag(TagType.KnowledgeBaseSyncIntegration, provider),
        TagType.KnowledgeBase,
        TagType.KnowledgeBaseAttachment,
      ],
    }),

    triggerKnowledgeBaseSync: builder.mutation<
      KnowledgeBaseSyncActiveRun,
      KnowledgeBaseSyncIntegrationActionArgs,
      KnowledgeBaseSyncRunResponse
    >({
      query: ({ integrationId }) => ({
        method: "POST",
        url: `/integrations/${integrationId}/sync`,
      }),
      transformResponse: (response) => response.data.run,
      async onQueryStarted({ provider }, { dispatch, queryFulfilled }) {
        try {
          const { data: run } = await queryFulfilled;
          dispatch(
            knowledgeBaseSyncIntegrationApi.util.updateQueryData(
              "knowledgeBaseSyncIntegrationStatus",
              provider,
              (draft) => {
                draft.active_sync_run = run;
              },
            ),
          );
        } catch {
          // The mutation error is rendered by the form.
        }
      },
    }),
  }),
});

/** RTK status subscription with polling enabled only for an active sync run. */
export function useKnowledgeBaseSyncIntegrationStatus(
  provider: KnowledgeBaseSyncProvider,
  enabled: boolean,
) {
  const arg = enabled ? provider : skipToken;
  const state =
    knowledgeBaseSyncIntegrationApi.endpoints.knowledgeBaseSyncIntegrationStatus.useQueryState(
      arg,
    );
  const subscription =
    knowledgeBaseSyncIntegrationApi.endpoints.knowledgeBaseSyncIntegrationStatus.useQuerySubscription(
      arg,
      {
        pollingInterval: state.currentData?.active_sync_run
          ? ACTIVE_SYNC_POLL_INTERVAL_MS
          : 0,
        refetchOnMountOrArgChange: true,
      },
    );

  return { ...state, refetch: subscription.refetch };
}

export const {
  useCreateKnowledgeBaseSyncIntegrationMutation,
  useDeleteKnowledgeBaseSyncIntegrationMutation,
  useTriggerKnowledgeBaseSyncMutation,
  useUpdateKnowledgeBaseSyncIntegrationMutation,
} = knowledgeBaseSyncIntegrationApi;
